/**
 * Add / Edit worker modal — thin config over the generic <FormModal>.
 * Add (POST /vendors/{vendorId}/workers) maps a NEW worker to a chosen vendor (needs vendor +
 * name + default_facility_id). Edit (PATCH /workers/{id}) changes category / facility / status /
 * shift — vendor and name are fixed on edit.
 */
import React, { useEffect, useState } from 'react';
import { FormModal, FieldDef } from '../../../components/manpower/FormModal';
import { useWorkerMutations } from '../hooks/useWorkerMutations';
import { useFacilities } from '../hooks/useFacilities';
import { useVendorData } from '../../manpower-vendors/hooks/useVendorData';
import type { Worker } from '../../../services/manpowerWorkersApi';

type Category = NonNullable<Worker['worker_category']>;
type Shift = NonNullable<Worker['shift_label']>;

interface WorkerModalProps {
	open: boolean;
	editing?: Worker | null;
	/** Drill-down sub-page: pre-fill + lock the vendor to this id on create. */
	lockedVendorId?: number;
	onClose: () => void;
	onSuccess: (message: string) => void;
}

export const WorkerModal: React.FC<WorkerModalProps> = ({ open, editing, lockedVendorId, onClose, onSuccess }) => {
	const isEdit = !!editing;
	const vendorLocked = !isEdit && lockedVendorId != null;
	const { addToVendor, update } = useWorkerMutations();
	const { data: vendorsResp } = useVendorData();
	const { data: facilities = [] } = useFacilities();
	const vendors = vendorsResp?.data ?? [];

	const [values, setValues] = useState<Record<string, string | number>>({});
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setValues({
				vendor_id: editing?.vendor_id ?? lockedVendorId ?? '',
				name: editing?.name ?? '',
				worker_category: editing?.worker_category ?? 'unskilled',
				default_facility_id: editing?.default_facility_id ?? '',
				shift_label: editing?.shift_label ?? '',
				shift_hours: editing?.shift_hours ?? '',
				status: editing?.status ?? 'active',
			});
			setError(null);
		}
	}, [open, editing, lockedVendorId]);

	const onChange = (name: string, value: string | number) =>
		setValues(prev => ({ ...prev, [name]: value }));

	const name = String(values.name ?? '').trim();
	const nameValid = name.length >= 2 && name.length <= 150;
	const facilityChosen = values.default_facility_id !== '' && values.default_facility_id != null;
	const vendorChosen = values.vendor_id !== '' && values.vendor_id != null;
	const canSubmit = isEdit ? facilityChosen : vendorChosen && nameValid && facilityChosen;
	const submitting = addToVendor.isPending || update.isPending;

	const categoryOpts = [
		{ value: 'unskilled', label: 'unskilled' },
		{ value: 'skilled', label: 'skilled' },
		{ value: 'supervisor', label: 'supervisor' },
	];
	const shiftOpts = [
		{ value: 'Day', label: 'Day' },
		{ value: 'Night', label: 'Night' },
	];

	const fields: FieldDef[] = [
		{
			name: 'vendor_id',
			label: 'Vendor',
			type: 'select',
			required: !isEdit,
			disabled: isEdit || vendorLocked,
			placeholder: 'Select a vendor…',
			options: vendors.map(v => ({ value: v.id as number, label: `${v.name}${v.city_name ? ` — ${v.city_name}` : ''}` })),
		},
		...(!isEdit
			? [
					{
						name: 'name',
						label: 'Name',
						type: 'text' as const,
						required: true,
						placeholder: 'Worker name',
						hint: !nameValid && name.length > 0 ? 'Name must be 2–150 characters.' : undefined,
					},
				]
			: []),
		{ name: 'worker_category', label: 'Category', type: 'select', options: categoryOpts },
		{
			name: 'default_facility_id',
			label: 'Default Facility',
			type: 'select',
			required: true,
			placeholder: 'Select a facility…',
			options: facilities.map(f => ({ value: f.id, label: f.location })),
		},
		{ name: 'shift_label', label: 'Shift', type: 'select', placeholder: '—', options: shiftOpts, half: true },
		{ name: 'shift_hours', label: 'Shift hours', type: 'number', placeholder: '1–24', half: true },
		...(isEdit
			? [
					{
						name: 'status',
						label: 'Status',
						type: 'select' as const,
						options: [
							{ value: 'active', label: 'active' },
							{ value: 'inactive', label: 'inactive' },
						],
					},
				]
			: []),
	];

	const handleSubmit = async () => {
		setError(null);
		const shift = values.shift_label === '' ? undefined : (values.shift_label as Shift);
		const hours = values.shift_hours === '' || values.shift_hours == null ? undefined : Number(values.shift_hours);
		try {
			if (isEdit && editing?.id != null) {
				await update.mutateAsync({
					id: editing.id,
					body: {
						worker_category: values.worker_category as Category,
						default_facility_id: facilityChosen ? Number(values.default_facility_id) : undefined,
						status: values.status as Worker['status'],
						shift_label: shift,
						shift_hours: hours,
					},
				});
				onSuccess('Worker updated');
			} else {
				await addToVendor.mutateAsync({
					vendorId: Number(values.vendor_id),
					body: {
						name,
						worker_category: values.worker_category as Category,
						default_facility_id: Number(values.default_facility_id),
						shift_label: shift,
						shift_hours: hours,
					},
				});
				onSuccess('Worker added to vendor');
			}
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Something went wrong');
		}
	};

	return (
		<FormModal
			open={open}
			title={isEdit ? 'Edit Worker' : 'Add Worker to Vendor'}
			fields={fields}
			values={values}
			onChange={onChange}
			canSubmit={canSubmit}
			submitting={submitting}
			submitLabel={isEdit ? 'Save changes' : 'Add worker'}
			error={error}
			onSubmit={handleSubmit}
			onClose={onClose}
		/>
	);
};

export default WorkerModal;
